import React, { useState } from 'react';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
import type { DocType, PropertyType } from '../types/loan';
interface SelectionScreenProps {
  initialDocType?: DocType | null;
  initialPropertyType?: PropertyType | null;
  onContinue: (docType: DocType, propertyType: PropertyType) => void;
}
const DOC_TYPES: DocType[] = ['IRRRL', 'FHA Streamline', 'Full Doc'];
const PROPERTY_TYPES: PropertyType[] = ['Condo', 'Manufactured', 'Other'];
function StepBadge({
  step,
  isComplete,
  isActive,
  label
}: {
  step: number;
  isComplete: boolean;
  isActive: boolean;
  label: string;
}) {
  return <div className="flex items-center gap-2 shrink-0" data-id="element-10">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${isComplete ? 'bg-teal-600 text-white' : isActive ? 'border border-amber-400 text-amber-600 bg-amber-50 ring-2 ring-amber-400/30 animate-pulse' : 'border border-slate-200 text-slate-400'}`} data-id="element-11">
        {isComplete ? <CheckIcon className="w-3.5 h-3.5" strokeWidth={3} data-id="element-12" /> : step}
      </div>
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${isComplete || isActive ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-13">
        {label}
      </span>
    </div>;
}
function SegmentedGroup<T extends string>({
  options,
  value,
  onChange
}: {
  options: readonly T[];
  value: T | null;
  onChange: (v: T) => void;
}) {
  return <div className="flex rounded-md border border-slate-200 bg-slate-50 p-0.5 transition-colors" data-id="element-14">
      {options.map(opt => {
      const selected = value === opt;
      return <button key={opt} type="button" onClick={() => onChange(opt)} className={`px-2.5 py-1 text-xs font-semibold rounded transition-colors ${selected ? 'bg-teal-700 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'}`} data-id="element-15">
            {opt}
          </button>;
    })}
    </div>;
}
export function SelectionScreen({
  initialDocType = null,
  initialPropertyType = null,
  onContinue
}: SelectionScreenProps) {
  const [docType, setDocType] = useState<DocType | null>(initialDocType);
  const [propertyType, setPropertyType] = useState<PropertyType | null>(initialPropertyType);
  const canContinue = docType !== null && propertyType !== null;
  return <div className="w-full bg-slate-50 p-4" data-id="element-16">
      <div className="w-full bg-white rounded-lg border border-slate-200 shadow-sm px-4 py-3 flex items-center gap-4 overflow-x-auto no-scrollbar" data-id="element-17">
        <div className="text-sm font-semibold text-slate-900 shrink-0" data-id="element-18">
          Loan setup
        </div>

        <div className="w-px h-4 bg-slate-200 shrink-0" data-id="element-19" />

        {/* Step 1 */}
        <div className="flex items-center gap-3 shrink-0" data-id="element-20">
          <StepBadge step={1} isComplete={!!docType} isActive={!docType} label="Doc Type" data-id="element-21" />
          <SegmentedGroup options={DOC_TYPES} value={docType} onChange={setDocType} data-id="element-22" />
        </div>

        {/* Connector */}
        <div className="h-px w-8 bg-slate-200 shrink-0" data-id="element-23" />

        {/* Step 2 */}
        <div className="flex items-center gap-3 shrink-0" data-id="element-24">
          <StepBadge step={2} isComplete={!!propertyType} isActive={!!docType && !propertyType} label="Property" data-id="element-25" />
          <SegmentedGroup options={PROPERTY_TYPES} value={propertyType} onChange={setPropertyType} data-id="element-26" />
        </div>

        {/* Continue */}
        <button type="button" disabled={!canContinue} onClick={() => canContinue && onContinue(docType!, propertyType!)} className={`ml-auto shrink-0 flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold transition-all ${canContinue ? 'bg-teal-700 text-white hover:bg-teal-800' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-27">
          Continue
          <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-28" />
        </button>
      </div>
    </div>;
}